import { t } from '@/i18n';

const TAG_REQUIRED_TICKERS = new Set(['XLM', 'XRP', 'BNBBSC']);
const CRYPTO_PROVIDER_CODES = new Set(['HP']);
const EXTERNAL_WITHDRAW_PROVIDER_CODES = new Set(['88P', 'BP', 'DCP', 'GTW', 'VX', 'AG']);
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const normalizeCurrencyCode = (value) => String(value || '').trim().toUpperCase();

export const PAYMENT_METHOD_PLACEHOLDER = '💳';

// `label` / `placeholder` (cuando el placeholder es texto, no un número de ejemplo) se resuelven
// en i18n (`wallet.fields.<key>.*`) con getters, así siguen el idioma activo aunque estas
// definiciones se armen una sola vez al cargar el módulo.
const FIELD_DEFINITIONS = {
  name: { type: 'text', required: true, maxLength: 50 },
  surname: { type: 'text', required: true, maxLength: 50 },
  email: { type: 'email', required: true, maxLength: 150, pattern: EMAIL_REGEX },
  phone: { placeholder: '+54 11 1234 5678', type: 'tel', required: false },
  taxId: { placeholder: '20123456789', type: 'text', required: true, maxLength: 15 },
  cbu: { placeholder: '0000003100099999999999', type: 'text', required: true, maxLength: 22 },
  cuitl: { placeholder: '20123456789', type: 'text', required: true, maxLength: 15 },
  accountHolder: { type: 'text', required: true, maxLength: 50 },
  payoutAddress: { type: 'text', required: true },
  payoutExtraId: { type: 'text', required: true },
};

const defineField = (key, overrides = {}) => {
  const definition = FIELD_DEFINITIONS[key];
  if (!definition) return null;
  const { placeholder, ...rest } = definition;
  const field = { key, ...rest, ...overrides };
  Object.defineProperty(field, 'label', {
    enumerable: true,
    get: () => t(`wallet.fields.${key}.label`),
  });
  Object.defineProperty(field, 'placeholder', {
    enumerable: true,
    get: () => placeholder ?? t(`wallet.fields.${key}.placeholder`),
  });
  if (key === 'email') {
    Object.defineProperty(field, 'patternMessage', {
      enumerable: true,
      get: () => t('wallet.validation.emailFormat'),
    });
  }
  return field;
};

const makeFields = (keys) => keys.map((key) => defineField(key)).filter(Boolean);

const MANUAL_DEPOSIT_DEFAULT_FIELDS = [
  ...makeFields(['name', 'surname', 'taxId']),
  defineField('email', { required: false }),
];
const WITHDRAW_ACCOUNT_FIELDS = makeFields(['cbu', 'cuitl', 'accountHolder']);

const DEPOSIT_FIELDS = {
  BP: makeFields(['name', 'taxId']),
  DCP: makeFields(['name', 'surname']),
  GTW: makeFields(['name', 'surname', 'email', 'taxId']),
};

const DEPOSIT_FIELDS_BY_CHANNEL = {
  GTW: {
    BTP: [
      ...makeFields(['name', 'surname', 'email']),
      defineField('taxId', { exactLength: 11 }),
    ],
    MTP: [
      ...makeFields(['name', 'surname', 'email']),
      defineField('taxId', { exactLength: 11 }),
    ],
    UNP: [
      ...makeFields(['name', 'surname', 'email']),
      defineField('taxId', { exactLength: 8 }),
    ],
  },
};

export const validateDynamicFieldValue = ({ field, value }) => {
  if (!field || typeof field !== 'object') {
    return '';
  }

  const label = String(field.label || field.key || t('wallet.validation.theField')).trim();
  const raw = String(value ?? '').trim();

  if (field.required && !raw) {
    return t('wallet.validation.required', { field: label });
  }

  if (!raw) {
    return '';
  }

  if (field.type === 'email' && field.pattern instanceof RegExp && !field.pattern.test(raw)) {
    return field.patternMessage || t('wallet.validation.emailFormat');
  }

  if (Number.isFinite(Number(field.exactLength))) {
    const exactLength = Number(field.exactLength);
    if (raw.length !== exactLength) {
      return t('wallet.validation.exactLength', { field: label, length: exactLength });
    }
  }

  if (Number.isFinite(Number(field.maxLength))) {
    const maxLength = Number(field.maxLength);
    if (raw.length > maxLength) {
      return t('wallet.validation.maxLength', { field: label, length: maxLength });
    }
  }

  return '';
};

export const resolveProviderFlow = ({ providerCode, operation, providerType }) => {
  const code = String(providerCode || '').trim().toUpperCase();
  const op = String(operation || '').trim().toLowerCase();

  if (op !== 'deposit' && op !== 'withdraw') {
    return 'unsupported';
  }

  if (CRYPTO_PROVIDER_CODES.has(code)) {
    return 'crypto';
  }

  const rawProviderType = typeof providerType === 'string'
    ? providerType
    : providerType?.[op];

  const normalizedType = String(rawProviderType || '').trim().toLowerCase();

  if (op === 'withdraw') {
    // Cashier withdraw view always uses form component and
    // delegates to external endpoint only for this explicit provider set.
    return EXTERNAL_WITHDRAW_PROVIDER_CODES.has(code) ? 'external' : 'manual';
  }

  // Cashier deposit flow: redirect -> external, everything else -> form/manual.
  if (normalizedType === 'redirect') {
    return 'external';
  }

  return 'manual';
};

export const getDynamicFields = ({ providerCode, operation, channelCode, flow }) => {
  const code = String(providerCode || '').trim().toUpperCase();
  const op = String(operation || '').trim().toLowerCase();
  const channel = String(channelCode || '').trim().toUpperCase();
  const normalizedFlow = String(flow || '').trim().toLowerCase();

  if (normalizedFlow === 'crypto') {
    return [];
  }

  if (op === 'deposit') {
    if (normalizedFlow === 'manual') {
      return MANUAL_DEPOSIT_DEFAULT_FIELDS;
    }

    if (normalizedFlow !== 'external') {
      return [];
    }

    const byChannel = DEPOSIT_FIELDS_BY_CHANNEL[code]?.[channel];
    if (Array.isArray(byChannel)) {
      return byChannel;
    }
    return DEPOSIT_FIELDS[code] || [];
  }

  if (op === 'withdraw') {
    return WITHDRAW_ACCOUNT_FIELDS;
  }

  return [];
};

export const requiresDestinationTag = (ticker) => TAG_REQUIRED_TICKERS.has(normalizeCurrencyCode(ticker));
